import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const qc = useQueryClient();
  const [wid, setWid] = useState(() => localStorage.getItem("cq_ws"));
  const { data: workspaces = [], isLoading, error, refetch } = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => api.get("/workspaces").then((r) => r.data),
  });

  useEffect(() => {
    if (!workspaces.length) return;
    if (!wid || !workspaces.find((w) => w.id === wid)) setWid(workspaces[0].id);
  }, [workspaces, wid]);

  useEffect(() => { if (wid) localStorage.setItem("cq_ws", wid); }, [wid]);

  const current = workspaces.find((w) => w.id === wid) || null;
  const { data: roleMap } = useQuery({ queryKey: ["roles"], queryFn: () => api.get("/roles").then((r) => r.data), staleTime: Infinity });
  const can = useCallback((perm) => !!roleMap?.roles?.[current?.role]?.includes(perm), [roleMap, current?.role]);
  const switchTo = useCallback((id) => { setWid(id); qc.removeQueries({ predicate: (q) => q.queryKey[0] === "ws" }); }, [qc]);
  const refresh = useCallback(() => qc.invalidateQueries({ queryKey: ["ws", wid] }), [qc, wid]);

  const value = useMemo(() => ({
    workspaces, current, wid: current?.id, role: current?.role, can, rolesReady: !!roleMap, isLoading, error, switchTo, refresh,
    reloadWorkspaces: refetch, path: (p) => `/workspaces/${current?.id}${p}`,
  }), [workspaces, current, can, isLoading, error, switchTo, refresh, refetch]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export const useWorkspace = () => useContext(WorkspaceContext);

export function useWsQuery(subpath, opts = {}) {
  const { wid, path } = useWorkspace();
  return useQuery({
    queryKey: ["ws", wid, subpath],
    queryFn: () => api.get(path(subpath)).then((r) => r.data),
    enabled: !!wid && (opts.enabled ?? true),
    ...opts,
  });
}
